import { Box, styled } from "@mui/material"
import { PropsWithChildren } from "react"
import { TableVariants } from "../interfaces"
import { BORDER_RADIUS, LEFT_ROW_INDICATOR } from "../configs"

type TableRowComponentProps = PropsWithChildren<{
    active: boolean
    colCount: number
    singleRow?: boolean
    rowIndicatorColor?: string
    transparent?: boolean
    transparentHighlight?: boolean
    variant?: TableVariants
    rowIndicatorWidth?: number
}>

function shouldForwardProp(prop: string) {
    return !["colCount", "active", "singleRow", "rowIndicatorColor", "transparent", "transparentHighlight"].includes(prop as string)
}

export const TableRowComponent = styled(Box, { shouldForwardProp })<TableRowComponentProps>((props) => {
    const { theme, colCount, active, singleRow, rowIndicatorColor, transparent, transparentHighlight, variant, rowIndicatorWidth } = props

    const elementListOverwrites = theme.overwrites?.components?.elementList ?? {}
    const overwritesBorderLeftWidth = elementListOverwrites?.backgroundColor?.borderLeftWidth
    const borderLeftWidth = rowIndicatorWidth ?? (overwritesBorderLeftWidth || LEFT_ROW_INDICATOR)

    const rowBorderColor = transparent ? "transparent" : rowIndicatorColor

    return {
        display: "contents",
        "> .Table-cell": {
            border: `1px solid ${transparent ? "transparent" : theme.palette.grey[50]}`,
            ":not(.Table-extendedCell)": {
                ...(singleRow && { borderBottom: "none" }),
            },
            "&:first-of-type": {
                borderLeft: `${borderLeftWidth}px solid ${rowBorderColor || theme.palette.grey[300]}`,
                paddingLeft: variant === "small" ? theme.spacing(0.5) : theme.spacing(1),
                borderRadius: `${BORDER_RADIUS} 0 0 0`,
            },
            [`:nth-of-type(${colCount})`]: {
                borderRadius: `0 ${BORDER_RADIUS} 0 0`,
                "&.borderOnlyOnTheLeft": {
                    borderRightColor: theme.palette.grey[200],
                },
            },
            ":not(:first-of-type):not(.Table-extendedCell)": {
                borderLeft: "none",
            },
            [`:not(:nth-of-type(${colCount})):not(.Table-extendedCell)`]: {
                borderRight: "none",
            },
        },
        "&.row-active > .Table-cell": {
            backgroundColor: `${theme.palette.grey[50]}`,
            borderColor: rowBorderColor || theme.palette.primary.main,
        },
        "&:hover > .Table-cell": {
            ...(!transparentHighlight && { borderColor: rowBorderColor || theme.palette.primary.light }),
        },
        "> .Table-extendedCell": {
            borderTop: "none",
            borderLeft: `${borderLeftWidth}px solid ${rowBorderColor || theme.palette.grey[300]}`,
            borderRadius: `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`,
            "&.borderOnlyOnTheLeft": {
                borderRightColor: theme.palette.grey[200],
            },
        },
    }
})
